// The teacher console's year, in the top bar: which academic year the lists
// below are showing, and — when the teacher looks after a class this year —
// "Class teacher · 10-A", which is what widens what they can read.
//
// One control for every teacher page, because a year chosen on My papers and
// silently forgotten on Analytics would show last year's papers beside this
// year's reports. The state is `useTeacherYear`'s.
import { useTeacherYear } from '@/hooks/useTeacherYear';

export function TeacherYearSwitcher() {
  const { years, year, readOnly, setYear } = useTeacherYear();
  if (!years || !year) return null;

  const classes = years.classTeacherOf.map((room) => room.name).join(', ');
  return (
    <div className="flex items-center gap-2">
      {classes && (
        <span
          className="hidden rounded-full px-2.5 py-0.5 text-fs-11 font-semibold md:inline"
          style={{ background: 'var(--bin-green-bg)', color: 'var(--bin-green-deep)' }}
          title="You look after this class: you read every subject's work in it and manage its roster."
        >
          Class teacher · {classes}
        </span>
      )}
      {years.years.length > 1 ? (
        <label className="flex items-center gap-1.5 text-xs font-semibold text-ink-soft">
          <span className="sr-only">Academic year</span>
          <select
            value={year.id}
            onChange={(event) => setYear(event.target.value)}
            className="rounded-full border border-line bg-card px-2.5 py-1 text-xs font-semibold text-ink outline-none focus:border-forest"
            style={readOnly ? { borderColor: 'var(--bin-orange-border)' } : undefined}
            title={
              readOnly
                ? 'A previous year: open and print its papers. Nothing can be changed.'
                : 'Your school’s current academic year'
            }
          >
            {years.years.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
                {option.current ? '' : ' (read-only)'}
              </option>
            ))}
          </select>
        </label>
      ) : (
        <span className="hidden text-xs font-semibold text-ink-soft sm:inline">{year.label}</span>
      )}
    </div>
  );
}

/**
 * Said once at the top of a teacher page while a previous year is on screen,
 * so nobody wonders why there is no "New paper".
 */
export function PreviousYearNotice() {
  const { year, readOnly, years, setYear } = useTeacherYear();
  if (!readOnly || !year || !years) return null;
  return (
    <div
      className="flex flex-wrap items-center gap-2 rounded-2xl border px-4 py-3 text-sm"
      style={{
        background: 'var(--bin-orange-bg)',
        borderColor: 'var(--bin-orange-border)',
        color: 'var(--bin-orange-deep)',
      }}
    >
      <span className="mr-auto">
        You are looking at <strong>{year.label}</strong>. Its papers can be opened, printed and
        downloaded; nothing in a previous year can be changed.
      </span>
      <button
        onClick={() => setYear(years.current.id)}
        className="rounded-lg border border-current px-3 py-1 text-xs font-semibold hover:opacity-80"
      >
        Back to {years.current.label}
      </button>
    </div>
  );
}
